import React, { useState } from 'react';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { employmentTypes } from '../../../data/hrDefaults';
import { EmployeeInput, EmploymentEventType, EmploymentStatus, HrEmployee } from '../../../types/hr';
import { todayIso } from '../../../utils/hrFormat';
import { Button } from '../../../components/ui/Button';
import { HrModal } from './HrModal';
type EventKind = Extract<EmploymentEventType, 'Promotion' | 'Transfer' | 'Status change' | 'Contract renewal' | 'Termination'>;
const inputClass = 'mt-1 block w-full rounded-md border border-slate-300 bg-white p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

/** Promotions, transfers, status changes, renewals and terminations — all kept in the employment history. */
export function EmploymentEventForm({
  employee,
  kind,
  onClose
}: {
  employee: HrEmployee;
  kind: EventKind;
  onClose: () => void;
}) {
  const {
    departments,
    positions,
    recordEmploymentEvent
  } = useHrPayroll();
  const [date, setDate] = useState(todayIso());
  const [departmentId, setDepartmentId] = useState(employee.departmentId);
  const [positionId, setPositionId] = useState(employee.positionId);
  const [status, setStatus] = useState<EmploymentStatus>(employee.status === 'Active' ? 'Suspended' : 'Active');
  const [expiry, setExpiry] = useState(employee.contractExpiry ?? '');
  const [employmentType, setEmploymentType] = useState(employee.employmentType);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const positionTitle = (id: string) => positions.find(p => p.id === id)?.title ?? '—';
  const deptName = (id: string) => departments.find(d => d.id === id)?.name ?? '—';
  const submit = async () => {
    setError('');
    let patch: Partial<EmployeeInput> = {};
    let summary = '';
    if (kind === 'Promotion' || kind === 'Transfer') {
      if (!positionId || positionId === employee.positionId && departmentId === employee.departmentId) return setError('Choose a different position or department.');
      patch = {
        departmentId,
        positionId
      };
      summary = `${positionTitle(employee.positionId)} (${deptName(employee.departmentId)}) → ${positionTitle(positionId)} (${deptName(departmentId)})`;
    } else if (kind === 'Status change') {
      patch = {
        status
      };
      summary = `${employee.status} → ${status}`;
    } else if (kind === 'Contract renewal') {
      if (!expiry) return setError('Enter the new contract expiry date.');
      patch = {
        contractExpiry: expiry,
        employmentType
      };
      summary = `${employmentType} until ${expiry}`;
    } else {
      if (!notes.trim()) return setError('Enter the reason for termination.');
      patch = {
        status: 'Terminated',
        terminationDate: date,
        terminationReason: notes.trim()
      };
      summary = `Terminated effective ${date}`;
    }
    setSaving(true);
    const result = await recordEmploymentEvent(employee.id, kind, date, notes.trim() ? `${summary} — ${notes.trim()}` : summary, patch);
    setSaving(false);
    if (!result.ok) return setError(result.error ?? 'Could not save.');
    onClose();
  };
  return <HrModal title={kind} subtitle={`${employee.firstName} ${employee.surname} · ${employee.employeeNumber}`} onClose={onClose} footer={<>
          {error && <p role="alert" className="mr-auto self-center text-sm text-red-700" data-id="element-819" data-mp="2cxaugarjz5dv">{error}</p>}
          <Button variant="outline" onClick={onClose} data-id="element-820" data-mp="28019p8f3zhwd">Cancel</Button>
          <Button variant={kind === 'Termination' ? 'danger' : 'primary'} onClick={submit} isLoading={saving} data-id="element-821" data-mp="22z3rivu3mnuv">Save {kind.toLowerCase()}</Button>
        </>} data-id="element-818" data-mp="2lrxwqy1x71lw">
      <div className="space-y-4" data-id="element-822" data-mp="1y23j4e9s190l">
        <div data-id="element-823" data-mp="1tzgqib5a7k2c">
          <label htmlFor="evt-date" className="block text-sm font-medium text-slate-700" data-id="element-824" data-mp="1u0081qr0s6fu">Effective date</label>
          <input id="evt-date" type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} data-id="element-825" data-mp="3sdrs7ztbuhd2" />
        </div>
        {(kind === 'Promotion' || kind === 'Transfer') && <div className="grid gap-4 sm:grid-cols-2" data-id="element-826" data-mp="3vgfsxncb57wy">
            <div data-id="element-827" data-mp="49d432jxzdpw">
              <label htmlFor="evt-dept" className="block text-sm font-medium text-slate-700" data-id="element-828" data-mp="49v326xzo61i">Department</label>
              <select id="evt-dept" value={departmentId} onChange={e => {
            setDepartmentId(e.target.value);
            setPositionId('');
          }} className={inputClass} data-id="element-829" data-mp="3meg9yphqftrd">
                {departments.map(d => <option key={d.id} value={d.id} data-id="element-830" data-mp="3cp53zbq5757p">{d.name}</option>)}
              </select>
            </div>
            <div data-id="element-831" data-mp="3hgepq18kloeb">
              <label htmlFor="evt-pos" className="block text-sm font-medium text-slate-700" data-id="element-832" data-mp="3hgwwhrsuzl0h">Position</label>
              <select id="evt-pos" value={positionId} onChange={e => setPositionId(e.target.value)} className={inputClass} data-id="element-833" data-mp="32stqiietg9bo">
                <option value="" data-id="element-834" data-mp="37nz85516r1ty">Select…</option>
                {positions.filter(p => p.departmentId === departmentId).map(p => <option key={p.id} value={p.id} data-id="element-835" data-mp="2ypdw7sq7yl25">{p.title}</option>)}
              </select>
            </div>
            {kind === 'Promotion' && <p className="text-xs text-slate-500 sm:col-span-2" data-id="element-836" data-mp="2f4wti0pvgzc9">Record any salary increase from the Salary &amp; Benefits tab with the same effective month.</p>}
          </div>}
        {kind === 'Status change' && <div data-id="element-837" data-mp="2o5v08qrp3kyj">
            <label htmlFor="evt-status" className="block text-sm font-medium text-slate-700" data-id="element-838" data-mp="2o275xpefeuir">New status</label>
            <select id="evt-status" value={status} onChange={e => setStatus(e.target.value as EmploymentStatus)} className={inputClass} data-id="element-839" data-mp="2y2pyd8k23jca">
              {(['Active', 'Suspended', 'Inactive'] as EmploymentStatus[]).filter(s => s !== employee.status).map(s => <option key={s} value={s} data-id="element-840" data-mp="37r7uhche529l">{s}</option>)}
            </select>
            <p className="mt-1 text-xs text-slate-500" data-id="element-841" data-mp="32swml7anp1u0">Inactive employees are excluded from payroll.</p>
          </div>}
        {kind === 'Contract renewal' && <div className="grid gap-4 sm:grid-cols-2" data-id="element-842" data-mp="3bu4hrghvlmjv">
            <div data-id="element-843" data-mp="3bvfi16c44c68">
              <label htmlFor="evt-type" className="block text-sm font-medium text-slate-700" data-id="element-844" data-mp="3bvxoswwei8se">Employment type</label>
              <select id="evt-type" value={employmentType} onChange={e => setEmploymentType(e.target.value as HrEmployee['employmentType'])} className={inputClass} data-id="element-845" data-mp="29evjtduqrm2t">
                {employmentTypes.map(t => <option key={t} value={t} data-id="element-846" data-mp="2ifjwo58efge4">{t}</option>)}
              </select>
            </div>
            <div data-id="element-847" data-mp="2na7ooe2m2sxg">
              <label htmlFor="evt-expiry" className="block text-sm font-medium text-slate-700" data-id="element-848" data-mp="2s76ggpysynsh">New expiry date</label>
              <input id="evt-expiry" type="date" value={expiry} onChange={e => setExpiry(e.target.value)} className={inputClass} data-id="element-849" data-mp="2x5rfwn68d3fv" />
            </div>
          </div>}
        <div data-id="element-850" data-mp="366z3aa77kjv6">
          <label htmlFor="evt-notes" className="block text-sm font-medium text-slate-700" data-id="element-851" data-mp="366pbqi6im4nd">{kind === 'Termination' ? 'Reason for termination *' : 'Notes'}</label>
          <textarea id="evt-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} className={inputClass} data-id="element-852" data-mp="17t5wneu39uqs" />
        </div>
        {kind === 'Termination' && <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-id="element-853" data-mp="14a6ybco8fcng">
            The employee is paid for the month of termination, then excluded from later payrolls. Their HR record, payslips and history are kept. Their HMS login is managed separately in Staff Management.
          </p>}
      </div>
    </HrModal>;
}